import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../context/ThemeContext';
import styles from './SplashScreen.module.css';

interface SplashScreenProps {
  onLoadComplete: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = ({ onLoadComplete }) => {
  const { theme } = useTheme();
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initializing...');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showRetry, setShowRetry] = useState(false);

  useEffect(() => {
    // Check online status
    const handleOnline = () => {
      setIsOnline(true);
      setShowRetry(false);
    };
    const handleOffline = () => {
      setIsOnline(false);
      setShowRetry(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline) {
      setLoadingText('No Internet Connection');
      return;
    }

    // Simulate loading process
    const loadingSteps = [
      { progress: 15, text: 'Loading resources...', duration: 400 },
      { progress: 30, text: 'Connecting to server...', duration: 600 },
      { progress: 50, text: 'Fetching content...', duration: 500 },
      { progress: 70, text: 'Loading articles...', duration: 600 },
      { progress: 85, text: 'Preparing interface...', duration: 400 },
      { progress: 95, text: 'Almost ready...', duration: 300 },
      { progress: 100, text: 'Done!', duration: 200 },
    ];

    let currentStep = 0;

    const loadNextStep = () => {
      if (currentStep < loadingSteps.length) {
        const step = loadingSteps[currentStep];
        setLoadingProgress(step.progress);
        setLoadingText(step.text);
        currentStep++;

        setTimeout(loadNextStep, step.duration);
      } else {
        // Loading complete
        setTimeout(() => {
          onLoadComplete();
        }, 400);
      }
    };

    loadNextStep();
  }, [isOnline, onLoadComplete]);

  const handleRetry = () => {
    if (navigator.onLine) {
      setIsOnline(true);
      setShowRetry(false);
      window.location.reload();
    } else {
      alert('Still no internet connection. Please check your network.');
    }
  };

  if (!isOnline) {
    return (
      <div className={styles.offlineScreen} data-theme={theme}>
        <div className={styles.offlineContainer}>
          <div className={styles.offlineIcon}>
            <svg width="120" height="120" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <line x1="8" y1="8" x2="16" y2="16" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <line x1="16" y1="8" x2="8" y2="16" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>

          <h1 className={styles.offlineTitle}>No Internet Connection</h1>
          <p className={styles.offlineText}>
            Oops! It looks like you're not connected to the internet.
            Please check your connection and try again.
          </p>

          <div className={styles.offlineTips}>
            <div className={styles.tip}>
              <span className={styles.tipIcon}>📡</span>
              <span>Check your WiFi or mobile data</span>
            </div>
            <div className={styles.tip}>
              <span className={styles.tipIcon}>🔌</span>
              <span>Verify your network cables</span>
            </div>
            <div className={styles.tip}>
              <span className={styles.tipIcon}>🔄</span>
              <span>Try restarting your router</span>
            </div>
          </div>

          <button className={styles.retryButton} onClick={handleRetry}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path 
                d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" 
                fill="currentColor"
              />
            </svg>
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.splashScreen} data-theme={theme}>
      <div className={styles.splashContent}>
        {/* Logo/Brand */}
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <div className={styles.logoCircle}>
              <svg width="100" height="100" viewBox="0 0 100 100" fill="none">
                <circle cx="50" cy="50" r="48" fill="#2563eb" stroke="#1d4ed8" strokeWidth="2"/>
                <path 
                  d="M50 25L68 50H32L50 25Z" 
                  fill="#ffffff"
                />
                <rect 
                  x="35" 
                  y="55" 
                  width="30" 
                  height="20" 
                  fill="#f97316"
                />
              </svg>
            </div>
          </div>
          <h1 className={styles.brandName}>Staruprahi</h1>
          <p className={styles.tagline}>Your Startup News Hub</p>
        </div>

        {/* Loading Bar */}
        <div className={styles.loadingContainer}>
          <div className={styles.loadingBarWrapper}>
            <div 
              className={styles.loadingBar}
              style={{ width: `${loadingProgress}%` }}
            >
              <div className={styles.loadingBarShine}></div>
            </div>
          </div>
          {/* <div className={styles.loadingInfo}>
            <p className={styles.loadingText}>{loadingText}</p>
            <p className={styles.loadingPercent}>{loadingProgress}%</p>
          </div> */}
        </div>

        {/* Animated Dots */}
        <div className={styles.dotsContainer}>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
          <span className={styles.dot}></span>
        </div>

        {/* Footer */}

      </div>
    </div>
  );
};

export default SplashScreen;